<template>
  <div
    class="FarmSlider farm-slider"
    :class="[`is--items-${farmCount}`, { 'is--logged-in': isLoggedIn }]"
  >
    <transition-group
      tag="div"
      :name="isMounted ? 'slide' : ''"
    >
      <FarmSliderItem
        v-for="(farm, i) in limitedFarmList"
        :key="farm.id"
        :visible="page === (i + 1)"
        :farm-data="farm"
        @mouseenter="onMouseEnter"
        @mouseleave="onMouseLeave"
      />
    </transition-group>
    <div
      v-show="farmCount > 1"
      class="farm-slider__navigation"
    >
      <FarmSliderProgress
        :key="page"
      />
      <ul class="farm-slider__pagination">
        <li
          v-for="p in farmCount"
          :key="p"
          class="farm-slider__pagination-item"
          :class="{ 'is--active': page === p }"
          @mouseenter="onMouseEnter"
          @mouseleave="onMouseLeave"
        >
          <button
            class="farm-slider__pagination-item-button"
            :aria-label="`Farm slider pagination button ${p}`"
            type="button"
            @click="setActivePage(p)"
          />
        </li>
      </ul>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onBeforeUnmount, onMounted, nextTick } from 'vue';
import { useFarmListOpenedSorted, useLogin } from 'Store';
import { FARM_TYPE } from 'Config/farm-type';
import FarmSliderItem from './FarmSliderItem.vue';
import FarmSliderProgress from './FarmSliderProgress.vue';

export default defineComponent({
  name: 'FarmSlider',
  components: {
    FarmSliderItem,
    FarmSliderProgress,
  },
  props: {
    limit: {
      type: [String, Number],
      validator: (val: string | number) => val >= 0,
    },
  },
  emits: ['slider-image-change'],
  setup(pops, { emit }) {
    const { isLoggedIn } = useLogin();
    let intervalId = 0;
    const page = ref(1);
    const isMounted = ref(false);
    const { farmListOpenedSorted } = useFarmListOpenedSorted();
    const farmListOpenedSortedWithoutDummy = computed(() => (
      farmListOpenedSorted.value.filter(item => item.type !== FARM_TYPE.DUMMY)
    ));

    const limitedFarmList = computed(() => farmListOpenedSortedWithoutDummy.value.slice(0, 3));
    const farmCount = computed(() => limitedFarmList.value.length);

    function emitImageChange() {
      const farm = limitedFarmList.value[page.value - 1];
      const imageUrl = farm?.thumbnail?.thumbnail?.url || '';
      emit('slider-image-change', imageUrl);
    }

    function setActivePage(pageNumber: number) {
      page.value = pageNumber;
      emitImageChange();
    }

    function setAutoPlay() {
      intervalId = setInterval(() => {
        const active = farmCount.value > page.value ? page.value + 1 : 1;
        setActivePage(active);
      }, 7000);
    }
    function onMouseEnter() {
      clearInterval(intervalId);
    }
    function onMouseLeave() {
      setAutoPlay();
    }

    onMounted(async () => {
      if (farmCount.value > 1) setAutoPlay();
      await nextTick();
      isMounted.value = true;
    });

    onBeforeUnmount(() => {
      clearInterval(intervalId);
    });

    emitImageChange();

    return {
      limitedFarmList,
      farmCount,
      page,
      isMounted,
      isLoggedIn,
      onMouseEnter,
      onMouseLeave,
      setActivePage,
    };
  },
});
</script>

<style lang="sass" scoped>
@import '~Styles/_variables'
.farm-slider
  width: 100%
  height: 565px !important
  position: relative
  &__container
    height: 100%
  &.is--logged-in
    @media screen and (max-width: 767px)
      height: 782px !important
  &:not(.is--logged-in)
    @media screen and (max-width: 767px)
      height: 784px !important
  &__navigation
    display: flex
    flex-direction: row
    align-items: center
    width: 100%
    max-width: 390px
    position: absolute
    bottom: -4px
    left: 0
    z-index: 3
    @media screen and (max-width: 767px)
      left: 50%
      transform: translate(-50%, 0%)
      bottom: -4px
  &__pagination
    margin-left: 0
    padding-left: 0
    display: flex
    flex-direction: row
    list-style-type: none
    justify-content: flex-end
  &__pagination-item
    margin-left: 15px
    cursor: pointer
    &.is--active
      .farm-slider__pagination-item-button
        border: 1px solid rgba($text_gray, 0.3)
        &:before
          opacity: 1
  &__pagination-item-button
    width: 14px
    height: 14px
    border: 1px solid transparent
    position: relative
    border-radius: 100px
    display: flex
    +mt(.2s)
    cursor: pointer
    &:hover,
    &:focus
      outline: none !important
    &:before
      content: ''
      position: absolute
      left: 50%
      top: 50%
      transform: translate(-50%, -50%)
      width: 8px
      height: 8px
      background: $text_gray
      border-radius: 100px
      opacity: 0.3
      +mt(.2s)
</style>
